import { useLots, useRegisterMovement, useUpdateAnimal } from '@/api/hooks';
import type { AnimalSummary } from '@/api/hooks';
import { Button } from '@/components/ui/Button';
import { DateInput } from '@/components/ui/DateInput';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { apiFetch, getErrorMessage } from '@/lib/api';
import { todayIso } from '@/lib/format';
import { animalToPatchBody } from '@/lib/pedigree';
import type { Animal } from '@/types/api';
import { useEffect, useState } from 'react';

export function AssignLotModal({
  open,
  onClose,
  propertyId,
  animal,
}: {
  open: boolean;
  onClose: () => void;
  propertyId: number;
  animal: AnimalSummary | null;
}) {
  const { data: lots } = useLots(propertyId);
  const register = useRegisterMovement('transfer');
  const updateAnimal = useUpdateAnimal(animal?.id ?? 0);
  const [error, setError] = useState('');
  const [destinationLotId, setDestinationLotId] = useState('');
  const [movementDate, setMovementDate] = useState(todayIso);
  const [notes, setNotes] = useState('');
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError('');
    setDestinationLotId('');
    setMovementDate(todayIso());
    setNotes('');
    setRemoving(false);
  }, [open, animal?.id]);

  if (!animal) return null;

  const title = animal.lot_id ? 'Mover para lote' : 'Adicionar ao lote';
  const displayName = [animal.internal_number, animal.animal_name].filter(Boolean).join(' · ');

  const lotOptions = (lots?.lots ?? [])
    .filter((l) => l.is_active && l.id !== animal.lot_id)
    .map((l) => ({ value: l.id, label: l.name }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!animal) return;
    setError('');

    if (!destinationLotId) {
      setError('Selecione o lote de destino.');
      return;
    }

    try {
      await register.mutateAsync({
        property_id: propertyId,
        movement_date: movementDate,
        scope: 'animal',
        notes: notes || null,
        animal_ids: [animal.id],
        destination_lot_id: Number(destinationLotId),
        is_off_property: false,
      });
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao atualizar lote do animal.'));
    }
  }

  async function handleRemoveFromLot() {
    if (!animal?.lot_id) return;
    setError('');
    setRemoving(true);
    try {
      const { animal: full } = await apiFetch<{ animal: Animal }>(`/animals/${animal.id}`);
      await updateAnimal.mutateAsync({
        ...animalToPatchBody(full),
        lot_id: null,
      });
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao retirar o animal do lote.'));
    } finally {
      setRemoving(false);
    }
  }

  const busy = register.isPending || removing;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="md"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          {animal.lot_id ? (
            <Button
              type="button"
              variant="danger"
              onClick={handleRemoveFromLot}
              loading={removing}
              disabled={busy}
            >
              Retirar do lote
            </Button>
          ) : null}
          <Button type="submit" form="assign-lot-form-modal" loading={register.isPending} disabled={busy}>
            {animal.lot_id ? 'Mover' : 'Adicionar'}
          </Button>
        </>
      }
    >
      <form id="assign-lot-form-modal" onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-manejai-bark/80">
          Animal: <span className="font-medium text-manejai-forest">{displayName}</span>
        </p>

        {animal.lot_name && (
          <p className="text-sm text-manejai-bark/60">
            Lote atual: <span className="font-medium">{animal.lot_name}</span>
          </p>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        <Select
          label="Lote destino"
          required
          value={destinationLotId}
          onChange={(e) => setDestinationLotId(e.target.value)}
          placeholder="Selecione o lote…"
          options={lotOptions}
        />
        <DateInput
          label="Data"
          required
          value={movementDate}
          onChange={setMovementDate}
        />
        <Input
          label="Observações"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Opcional"
        />
      </form>
    </Modal>
  );
}
